/**
 * Change Tracker Utility
 * Track changes between initial form values and current values
 */
import { FormChange } from '../types'

/**
 * Compare initial values with current values and generate a list of changes
 * @param initialValues - The initial form values
 * @param currentValues - The current form values
 * @returns Array of changes with field name, old value, and new value
 */
export const getFormChanges = (
  initialValues: Record<string, any> = {},
  currentValues: Record<string, any> = {}
): FormChange[] => {
  const changes: FormChange[] = []
  const allFields = new Set([
    ...Object.keys(initialValues),
    ...Object.keys(currentValues)
  ])

  allFields.forEach(field => {
    const initialValue = initialValues[field]
    const currentValue = currentValues[field]

    // Skip if values are the same
    if (isEqual(initialValue, currentValue)) {
      return
    }

    changes.push({
      field,
      oldValue: initialValue,
      newValue: currentValue
    })
  })

  return changes
}

/**
 * Check if two values are equal (handles arrays and objects)
 */
const isEqual = (value1: any, value2: any): boolean => {
  // Handle null/undefined
  if (value1 === value2) return true
  if (value1 === null || value2 === null) return false
  if (value1 === undefined || value2 === undefined) return false

  // Handle dates
  if (value1 instanceof Date && value2 instanceof Date) {
    return value1.getTime() === value2.getTime()
  }

  // Handle arrays
  if (Array.isArray(value1) && Array.isArray(value2)) {
    if (value1.length !== value2.length) return false

    // Sort arrays if they contain primitives
    if (
      value1.every(item => isPrimitive(item)) &&
      value2.every(item => isPrimitive(item))
    ) {
      const sorted1 = [...value1].sort()
      const sorted2 = [...value2].sort()
      return sorted1.every((item, index) => item === sorted2[index])
    }

    // Otherwise compare each item
    return value1.every((item, index) => isEqual(item, value2[index]))
  }

  // Handle objects
  if (isObject(value1) && isObject(value2)) {
    const keys1 = Object.keys(value1)
    const keys2 = Object.keys(value2)

    if (keys1.length !== keys2.length) return false

    return keys1.every(
      key =>
        Object.prototype.hasOwnProperty.call(value2, key) &&
        isEqual(value1[key], value2[key])
    )
  }

  return false
}

/**
 * Check if a value is a primitive type
 */
const isPrimitive = (value: any): boolean => {
  return (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    value === null ||
    value === undefined
  )
}

/**
 * Check if a value is an object (not array, null, or function)
 */
const isObject = (value: any): boolean => {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    !(value instanceof Date)
  )
}

/**
 * Format changes for display
 */
export const formatChanges = (changes: FormChange[]): string => {
  if (changes.length === 0) return 'No changes'

  return changes
    .map(change => {
      const oldValue = formatValue(change.oldValue)
      const newValue = formatValue(change.newValue)
      return `${change.field}: ${oldValue} → ${newValue}`
    })
    .join('\n')
}

/**
 * Format a value for display
 */
const formatValue = (value: any): string => {
  if (value === undefined || value === null) return '-'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}
